/**
 * ActivationsMenuValidation - Per-aggregator menu validation dashboard (ITRTL-720)
 *
 * Cloned from MenuValidation, with two key differences:
 *  1. Data source: POST activations-bff /api/v1/menu-validation { menuId, channel }
 *     instead of web-order GET /test/menus/{menuId}/validations.
 *  2. Columns/filters are DYNAMIC, built from response metadata.applicableValidations
 *     (the set of dimensions the selected aggregator actually validates).
 *
 * Query params: ?menuId=..&channel=.. (channel = URL-encoded PlatformChannel display name)
 *
 * Shared/unchanged components (AssociatedItemsModal, ItemPreviewPanel) are reused directly
 * from the MenuValidation scene to avoid duplication.
 */

import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import queryString from "query-string";
import Loader from "../../components/Loader";
import { toast } from "../../components/Toast";
import ActivationsValidationSummary from "./ActivationsValidationSummary";
import AssociatedItemsModal from "../MenuValidation/AssociatedItemsModal";
import ItemPreviewPanel from "../MenuValidation/ItemPreviewPanel";
import ValidationTable from "./ValidationTable";

// Cap the validation request so a hung BFF doesn't leave the spinner running forever.
const REQUEST_TIMEOUT_MS = 30000;

const ActivationsMenuValidation = () => {
  const location = useLocation();

  const [validationResults, setValidationResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [menuId, setMenuId] = useState(null);
  const [selectedModal, setSelectedModal] = useState(null);

  // Panel state management with sourceContext tracking (same as MenuValidation)
  const [panelState, setPanelState] = useState({
    isOpen: false,
    itemUrl: "",
    itemName: "",
    itemId: "",
    sourceContext: null // 'table' or 'modal'
  });

  useEffect(() => {
    document.title = "grubcenter-activations-menu-validation";
  }, []);

  useEffect(() => {
    const params = queryString.parse(location.search);

    // The link carries the PlatformChannel display name; the BFF resolves it to a rule set.
    if (!params.menuId || !params.channel) {
      setErrorMessage("This page requires both a menuId and a channel in the URL.");
      return;
    }

    setMenuId(params.menuId);
    fetchValidationResults(params.menuId, params.channel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const fetchValidationResults = async (menuIdParam, channelParam) => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const base = process.env.REACT_APP_ACTIVATIONS_API_URL;
      const url = `${base}/api/v1/menu-validation`;

      const body = { menuId: menuIdParam, channel: channelParam };

      const response = await axios.post(url, body, { timeout: REQUEST_TIMEOUT_MS });

      setValidationResults(response.data);
    } catch (error) {
      setErrorMessage("We couldn't load the validation results. Please refresh to try again.");
      toast.error(`Failed to fetch validation results: ${error.message}`, { autoClose: false });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setSelectedModal(null);
    if (panelState.isOpen && panelState.sourceContext === "modal") {
      handleClosePanel();
    }
  };

  // Handle opening modal from table
  const handleOpenModal = (item) => {
    if (panelState.isOpen && panelState.sourceContext === "table") {
      handleClosePanel();
    }
    setSelectedModal(item);
  };

  // Panel handlers with sourceContext tracking
  const handleOpenPanel = (item, sourceContext = "table") => {
    const locale = validationResults?.baseLanguage
      ? validationResults.baseLanguage.toLowerCase()
      : "en-us";
    const url = `/item-translated/${item.categoryId}/${item.id}/0?locale=${encodeURIComponent(locale)}&menuId=${encodeURIComponent(menuId)}&hideBack=true`;

    if (sourceContext === "table" && selectedModal) {
      setSelectedModal(null);
    }

    setPanelState({
      isOpen: true,
      itemUrl: url,
      itemName: item.name || "Item Details",
      itemId: item.id,
      sourceContext
    });
  };

  const handleClosePanel = () => {
    setPanelState({
      isOpen: false,
      itemUrl: "",
      itemName: "",
      itemId: "",
      sourceContext: null
    });
  };

  const hasRuleSet = validationResults && validationResults.hasRuleSet !== false;
  const applicableValidations = validationResults?.metadata?.applicableValidations || [];

  return (
    <div className="ls-validation">
      {isLoading && <Loader />}

      {!isLoading && errorMessage && (
        <div className="ls-validation-header-container">
          <div className="ls-validation-header">
            <h1 className="ls-validation-title">Menu Validation</h1>
            <p className="ls-validation-subtitle">{errorMessage}</p>
          </div>
        </div>
      )}

      {!isLoading && !errorMessage && validationResults && !hasRuleSet && (
        <div className="ls-validation-header-container">
          <div className="ls-validation-header">
            <h1 className="ls-validation-title">
              {validationResults.menuName || "Menu Validation"}
            </h1>
            <p className="ls-validation-subtitle">
              No validation rules are configured for
              {validationResults.channel ? ` ${validationResults.channel}` : " this aggregator"}.
            </p>
          </div>
        </div>
      )}

      {!isLoading && !errorMessage && validationResults && hasRuleSet && (
        <>
          <div className="ls-validation-header-container">
            <div className="ls-validation-header">
              {validationResults.channel && (
                <span className="ls-validation-channel-badge">
                  {validationResults.channel}
                </span>
              )}
              <h1 className="ls-validation-title">
                {validationResults.menuName || "Menu Validation"}
              </h1>
              <p className="ls-validation-subtitle">Summary of Missing Fields</p>
            </div>
          </div>

          <ActivationsValidationSummary
            results={validationResults.validationResults}
            applicableValidations={applicableValidations}
          />

          {/* Container for side-by-side layout when panel open from TABLE only */}
          <div className={`ls-validation-content-container ${panelState.isOpen && panelState.sourceContext === "table" ? "ls-validation-content-container--panel-open" : ""}`}>
            <ValidationTable
              validationResults={validationResults.validationResults}
              applicableValidations={applicableValidations}
              metadata={validationResults.metadata}
              onOpenModal={handleOpenModal}
              onOpenPanel={(item) => handleOpenPanel(item, "table")}
            />

            {panelState.isOpen && panelState.sourceContext === "table" && (
              <ItemPreviewPanel
                isOpen={panelState.isOpen}
                itemUrl={panelState.itemUrl}
                itemName={panelState.itemName}
                onClose={handleClosePanel}
              />
            )}
          </div>

          {selectedModal && (
            <AssociatedItemsModal
              validationItem={selectedModal}
              onClose={handleCloseModal}
              baseLanguage={validationResults.baseLanguage}
              menuId={menuId}
              onOpenPanel={(item) => handleOpenPanel(item, "modal")}
              isPreviewOpen={panelState.isOpen && panelState.sourceContext === "modal"}
            />
          )}

          {panelState.isOpen && panelState.sourceContext === "modal" && (
            <ItemPreviewPanel
              isOpen={panelState.isOpen}
              itemUrl={panelState.itemUrl}
              itemName={panelState.itemName}
              onClose={handleClosePanel}
              isModalContext={true}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ActivationsMenuValidation;
